// Block editor for after-hero content blocks (per page).
// Reads/writes settings key `page_blocks:{pageId}`.
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatError } from "../../api";
import { FileUploader, GalleryUploader } from "../../components/FileUploader";
import { BLOCK_TYPES, BLOCK_TYPE_LABELS, newBlock } from "../../components/PageBlocks";
import { LabeledInput, LabeledTextarea, LabeledSelect } from "./_inputs";

export const BlocksEditor = ({ page }) => {
  const settingsKey = `page_blocks:${page.id}`;
  const [blocks, setBlocks] = useState([]);
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let active = true;
    api
      .get(`/settings/${encodeURIComponent(settingsKey)}`)
      .then((r) => {
        if (!active) return;
        const v = r.data?.value;
        const arr = Array.isArray(v) ? v : [];
        setBlocks(arr);
        setSaved(arr);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [settingsKey]);

  const dirty = JSON.stringify(blocks) !== JSON.stringify(saved);

  const save = async () => {
    setLoading(true);
    setStatus(null);
    try {
      await api.put(`/admin/settings/${encodeURIComponent(settingsKey)}`, { value: blocks });
      const verify = await api.get(`/settings/${encodeURIComponent(settingsKey)}`);
      const persisted = verify.data?.value;
      const ok = Array.isArray(persisted) && persisted.length === blocks.length;
      setSaved(blocks);
      setStatus({
        kind: "ok",
        message: ok
          ? `${blocks.length} block${blocks.length === 1 ? "" : "s"} saved & verified — live at ${page.path}`
          : "Save returned 200 but server still shows old data. Retry, or contact support.",
        at: new Date(),
      });
      toast.success("Content blocks saved.");
    } catch (e) {
      const httpStatus = e?.response?.status;
      const detail = formatError(e?.response?.data?.detail);
      const message =
        httpStatus === 401 || httpStatus === 403
          ? "Session expired — log out, log back in, then save again."
          : detail
          ? `Save failed (HTTP ${httpStatus || "?"}): ${detail}`
          : `Save failed: ${e?.message || "Network error"}.`;
      setStatus({ kind: "err", message, at: new Date() });
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const addBlock = (type) => setBlocks([...blocks, newBlock(type)]);
  const removeBlock = (id) => setBlocks(blocks.filter((b) => b.id !== id));
  const updateBlock = (id, patch) =>
    setBlocks(blocks.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  const moveBlock = (id, dir) => {
    const idx = blocks.findIndex((b) => b.id === id);
    const j = idx + dir;
    if (idx < 0 || j < 0 || j >= blocks.length) return;
    const next = [...blocks];
    [next[idx], next[j]] = [next[j], next[idx]];
    setBlocks(next);
  };

  return (
    <div className="border-4 border-asphalt bg-bone shadow-hard p-6 space-y-5" data-testid={`blocks-editor-${page.id}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="font-display text-2xl uppercase tracking-tight">Content Blocks &middot; below the hero</div>
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink">
          {blocks.length} block{blocks.length === 1 ? "" : "s"}
        </span>
      </div>
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink">
        Add as many blocks as you want. They render in order, below the hero. Use the arrows to reorder.
      </p>

      <div className="space-y-4">
        {blocks.map((b, i) => (
          <BlockEditCard
            key={b.id}
            block={b}
            index={i}
            total={blocks.length}
            onChange={(patch) => updateBlock(b.id, patch)}
            onRemove={() => removeBlock(b.id)}
            onMove={(dir) => moveBlock(b.id, dir)}
          />
        ))}
        {blocks.length === 0 && (
          <div className="border-2 border-dashed border-asphalt p-6 text-center font-mono text-xs uppercase tracking-[0.25em] text-ink">
            &#10022; No blocks yet. Add one below.
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2 pt-3 border-t-2 border-asphalt/30">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] mr-2 self-center">Add block:</div>
        {BLOCK_TYPES.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => addBlock(t)}
            data-testid={`add-block-${t}`}
            className="border-2 border-asphalt px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] hover:bg-asphalt hover:text-bone"
          >
            + {BLOCK_TYPE_LABELS[t]}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-3 pt-3 border-t-2 border-asphalt/30 items-start">
        <button
          type="button"
          disabled={loading || !dirty}
          onClick={save}
          data-testid={`blocks-editor-${page.id}-save`}
          className="bg-asphalt text-bone border-2 border-asphalt px-5 py-3 font-display tracking-wider uppercase text-lg shadow-hard hover:bg-terra disabled:opacity-50"
        >
          {loading ? "Saving…" : dirty ? "Save blocks" : "All saved"}
        </button>
        <a
          href={page.path}
          target="_blank"
          rel="noreferrer"
          className="font-mono text-[10px] uppercase tracking-[0.25em] border-2 border-asphalt px-3 py-1.5 hover:bg-asphalt hover:text-bone self-center"
        >
          &#8599; Open preview
        </a>
        {status && (
          <div
            className={`flex-1 min-w-[220px] border-2 px-3 py-2 font-mono text-[11px] uppercase tracking-[0.18em] leading-snug ${
              status.kind === "ok"
                ? "border-asphalt bg-bone text-asphalt"
                : "border-terra bg-terra/10 text-terra"
            }`}
            data-testid={`blocks-editor-${page.id}-status`}
          >
            <div className="text-[10px] opacity-70 mb-0.5">
              {status.at.toLocaleTimeString()} — {status.kind === "ok" ? "✓ saved" : "✗ failed"}
            </div>
            <div className="normal-case tracking-normal text-xs">{status.message}</div>
          </div>
        )}
      </div>
    </div>
  );
};

const BlockEditCard = ({ block, index, total, onChange, onRemove, onMove }) => {
  return (
    <div className="border-2 border-asphalt bg-card-light p-4 space-y-3" data-testid={`block-card-${index}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] bg-asphalt text-bone px-2 py-1">
          #{index + 1} &middot; {BLOCK_TYPE_LABELS[block.type] || block.type}
        </span>
        <div className="ml-auto flex gap-1">
          <button type="button" onClick={() => onMove(-1)} disabled={index === 0} className="border-2 border-asphalt px-2 py-1 font-mono text-[10px] disabled:opacity-30" data-testid={`block-${index}-up`} title="Move up">↑</button>
          <button type="button" onClick={() => onMove(1)} disabled={index === total - 1} className="border-2 border-asphalt px-2 py-1 font-mono text-[10px] disabled:opacity-30" data-testid={`block-${index}-down`} title="Move down">↓</button>
          <button type="button" onClick={onRemove} className="border-2 border-terra bg-terra text-bone px-2 py-1 font-mono text-[10px]" data-testid={`block-${index}-remove`} title="Delete">×</button>
        </div>
      </div>

      {block.type !== "divider" && (
        <div className="grid sm:grid-cols-2 gap-3">
          <LabeledInput label="Heading" value={block.heading} onChange={(v) => onChange({ heading: v })} testid={`block-${index}-heading`} />
          <LabeledSelect
            label="Background"
            value={block.background}
            onChange={(v) => onChange({ background: v })}
            options={[
              { value: "bone", label: "Bone (default)" },
              { value: "card-light", label: "Card Light" },
              { value: "asphalt", label: "Asphalt (dark)" },
              { value: "terra", label: "Terra (red)" },
            ]}
            testid={`block-${index}-background`}
          />
        </div>
      )}

      {block.type !== "image" && block.type !== "video" && block.type !== "divider" && (
        <LabeledTextarea label="Body" rows={4} value={block.body} onChange={(v) => onChange({ body: v })} testid={`block-${index}-body`} />
      )}

      {(block.type === "image" || block.type === "media-row") && (
        <FileUploader
          accept="image"
          label="Image"
          value={block.media_url}
          onChange={(url) => onChange({ media_url: url })}
          testid={`block-${index}-image`}
        />
      )}
      {block.type === "video" && (
        <>
          <FileUploader
            accept="video"
            label="Video file"
            value={block.media_url}
            onChange={(url) => onChange({ media_url: url })}
            testid={`block-${index}-video`}
          />
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink">
            Or paste a YouTube URL / id in the field above instead of uploading.
          </p>
        </>
      )}

      {block.type === "gallery" && (
        <GalleryUploader
          label="Gallery images"
          value={Array.isArray(block.gallery) ? block.gallery : []}
          onChange={(arr) => onChange({ gallery: arr })}
          testid={`block-${index}-gallery`}
        />
      )}

      {block.type === "divider" && (
        <LabeledSelect
          label="Divider style"
          value={block.divider_style || "tape"}
          onChange={(v) => onChange({ divider_style: v })}
          options={[
            { value: "tape", label: "Striped tape (default)" },
            { value: "rule", label: "Hard rule" },
            { value: "stars", label: "Three stars" },
          ]}
          testid={`block-${index}-divider-style`}
        />
      )}

      {block.type === "media-row" && (
        <LabeledSelect
          label="Media side"
          value={block.media_side || "left"}
          onChange={(v) => onChange({ media_side: v })}
          options={[
            { value: "left", label: "Image on left" },
            { value: "right", label: "Image on right" },
          ]}
          testid={`block-${index}-media-side`}
        />
      )}

      {(block.type === "text" || block.type === "image" || block.type === "video") && (
        <LabeledSelect
          label="Align"
          value={block.align || "left"}
          onChange={(v) => onChange({ align: v })}
          options={[
            { value: "left", label: "Left" },
            { value: "center", label: "Center" },
            { value: "right", label: "Right" },
          ]}
          testid={`block-${index}-align`}
        />
      )}

      {(block.type === "cta" || block.type === "media-row") && (
        <div className="grid sm:grid-cols-2 gap-3">
          <LabeledInput label="Button label" value={block.cta_label} onChange={(v) => onChange({ cta_label: v })} testid={`block-${index}-cta-label`} />
          <LabeledInput label="Button URL" value={block.cta_href} onChange={(v) => onChange({ cta_href: v })} testid={`block-${index}-cta-href`} placeholder="/shop or https://..." />
        </div>
      )}
    </div>
  );
};
